import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Declarar gtag en el objeto window
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set' | 'consent',
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
    updateConsent?: (analytics: boolean, ads: boolean) => void;
  }
}

// Función para inicializar Google Analytics
export const initializeGoogleAnalytics = (measurementId: string) => {
  // Verificar si ya está inicializado
  if (typeof window.gtag === 'function') return;

  // Crear dataLayer si no existe
  window.dataLayer = window.dataLayer || [];
  
  // Función gtag
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Configurar fecha y hora
  window.gtag('js', new Date().toISOString());
  
  // Configurar GA4
  window.gtag('config', measurementId, {
    // Configuración de privacidad
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    
    // Configuración de rendimiento
    send_page_view: false, // Lo manejamos manualmente
    
    // Configuración específica del sitio
    custom_map: {
      'custom_parameter_1': 'page_category',
      'custom_parameter_2': 'user_type',
      'custom_parameter_3': 'service_interest'
    }
  });
};

// Hook para tracking de páginas
export const usePageTracking = (measurementId: string) => {
  const location = useLocation();

  useEffect(() => {
    if (!measurementId || typeof window.gtag !== 'function') return;

    // Track page view
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname,
      page_category: getPageCategory(location.pathname),
      user_type: 'visitor'
    });
  }, [location, measurementId]);
};

// Hook para tracking de eventos
export const useEventTracking = () => {
  const trackEvent = (
    eventName: string,
    parameters: Record<string, any> = {}
  ) => {
    if (typeof window.gtag !== 'function') return;

    window.gtag('event', eventName, {
      ...parameters,
      event_category: parameters.category || 'engagement',
      event_label: parameters.label || '',
      value: parameters.value || 0
    });
  };

  return { trackEvent };
};

// Función para obtener categoría de página
const getPageCategory = (pathname: string): string => {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/servicios')) return 'services';
  if (pathname.startsWith('/especialistas')) return 'specialists';
  if (pathname.startsWith('/contacto')) return 'contact';
  if (pathname.startsWith('/quienes-somos')) return 'about';
  if (pathname.startsWith('/trastornos')) return 'disorders';
  if (pathname.startsWith('/condiciones-del-desarrollo')) return 'development-conditions';
  if (pathname.startsWith('/nuestras-pruebas')) return 'tests';
  return 'other';
};

// Función para tracking de formularios
export const trackFormSubmission = (formName: string, success: boolean = true) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'form_submit', {
    event_category: 'engagement',
    event_label: formName,
    form_name: formName,
    success: success,
    value: success ? 1 : 0
  });
};

// Función para tracking de clics en botones
export const trackButtonClick = (buttonName: string, location: string) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'click', {
    event_category: 'engagement',
    event_label: buttonName,
    button_name: buttonName,
    button_location: location
  });
};

// Función para tracking de llamadas telefónicas
export const trackPhoneCall = (phoneNumber: string) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'phone_call', {
    event_category: 'conversion',
    event_label: phoneNumber,
    phone_number: phoneNumber,
    value: 1
  });
};

// Función para tracking de emails
export const trackEmailClick = (emailAddress: string) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'email_click', {
    event_category: 'conversion',
    event_label: emailAddress,
    email_address: emailAddress,
    value: 1
  });
};

// Función para tracking de Core Web Vitals
export const trackCoreWebVitals = () => {
  if (typeof window.gtag !== 'function') return;

  // LCP (Largest Contentful Paint)
  new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    const lastEntry = entries[entries.length - 1];
    
    window.gtag('event', 'web_vitals', {
      event_category: 'Web Vitals',
      event_label: 'LCP',
      value: Math.round(lastEntry.startTime),
      metric_id: 'LCP',
      metric_value: Math.round(lastEntry.startTime),
      metric_delta: Math.round(lastEntry.startTime)
    });
  }).observe({ entryTypes: ['largest-contentful-paint'] });

  // FID (First Input Delay)
  new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    entries.forEach((entry) => {
      const fidEntry = entry as PerformanceEventTiming;
      window.gtag('event', 'web_vitals', {
        event_category: 'Web Vitals',
        event_label: 'FID',
        value: Math.round(fidEntry.processingStart - fidEntry.startTime),
        metric_id: 'FID',
        metric_value: Math.round(fidEntry.processingStart - fidEntry.startTime),
        metric_delta: Math.round(fidEntry.processingStart - fidEntry.startTime)
      });
    });
  }).observe({ entryTypes: ['first-input'] });

  // CLS (Cumulative Layout Shift)
  let clsValue = 0;
  new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    entries.forEach((entry) => {
      const clsEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
      if (!clsEntry.hadRecentInput) {
        clsValue += clsEntry.value || 0;
      }
    });
    
    window.gtag('event', 'web_vitals', {
      event_category: 'Web Vitals',
      event_label: 'CLS',
      value: Math.round(clsValue * 1000),
      metric_id: 'CLS',
      metric_value: Math.round(clsValue * 1000),
      metric_delta: Math.round(clsValue * 1000)
    });
  }).observe({ entryTypes: ['layout-shift'] });
};
